// src/utils/cadence.js
//
// Calendar primitives for the session-cadence admin page. Implements the
// fixed-anchored-block model from docs/WEEK_INTERVAL_AND_WEEK_START.md
// (Part C) in the backend repo.
//
// A session period is a FIXED calendar block shared by the whole group,
// anchored to a start date -- not a per-user rolling window. Given an anchor
// A and interval I weeks (block length L = 7*I days):
//
//   n           = floor((today - A) / L)      which block we are in, 0-based
//   block_start = A + n*L
//   block_end   = block_start + L             exclusive; also the next unlock
//
// This module holds only what the UI needs to DISPLAY a cadence. The
// preview/violation logic that decides whether a proposed change is safe is
// deliberately NOT here -- per the design doc that math is server-owned so the
// UI cannot disagree with what is actually enforced. Until the backend ships
// it, the server computes it -- see POST /group/{id}/cadence/preview.
//
// ---------------------------------------------------------------------------
// Timezone note, which is the whole reason this file does its own date math:
// `new Date("2026-07-20")` parses as UTC midnight, so in America/Chicago
// (UTC-5/6) `.getDay()` reports the PREVIOUS day -- a Monday anchor would
// render as Sunday and every start-day check would be off by one. Every date
// here is a plain calendar date with no time component, built and read purely
// through the UTC accessors, so the running machine's timezone cannot shift it.

const MS_PER_DAY = 86400000;

// Weekday names indexed the way Python's date.weekday() does it (Monday = 0),
// so the values sent to and read from the backend need no translation. Note
// that JS getUTCDay() is Sunday = 0; convert with weekdayIndex() below, never
// by reading getUTCDay() directly.
export const WEEKDAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

// "YYYY-MM-DD" -> Date at UTC midnight. Returns null for empty/invalid input
// so callers can treat "no date chosen yet" as a normal state.
export function parseDate(iso) {
  if (!iso) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso).trim());
  if (!m) return null;
  const [, y, mo, d] = m;
  const date = new Date(Date.UTC(Number(y), Number(mo) - 1, Number(d)));
  // Rejects overflow like 2026-02-30, which Date.UTC would silently roll over.
  if (date.getUTCMonth() !== Number(mo) - 1 || date.getUTCDate() !== Number(d)) {
    return null;
  }
  return date;
}

// Date -> "YYYY-MM-DD". The inverse of parseDate; safe to feed to <input type="date">.
export function toISO(date) {
  if (!date) return "";
  const y = String(date.getUTCFullYear()).padStart(4, "0");
  const m = String(date.getUTCMonth() + 1).padStart(2, "0");
  const d = String(date.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function addDays(date, n) {
  return new Date(date.getTime() + n * MS_PER_DAY);
}

// Whole days from b to a (a - b). Both operands are UTC midnights, so this is
// exact -- no DST hour to round away.
export function diffDays(a, b) {
  return Math.round((a.getTime() - b.getTime()) / MS_PER_DAY);
}

// Monday = 0, matching Python's date.weekday() and the start_day values the
// backend will store.
export function weekdayIndex(date) {
  return (date.getUTCDay() + 6) % 7;
}

export function weekdayName(date) {
  return WEEKDAYS[weekdayIndex(date)];
}

// Today as a UTC-midnight calendar date, read off the LOCAL clock. Deliberately
// not `new Date()` truncated in UTC: an admin in Chicago at 20:00 on Aug 3 is
// still on Aug 3, and UTC would already call it Aug 4.
export function today() {
  const now = new Date();
  return new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
}

// -- Block math (Part C.2) --------------------------------------------------

// The fixed block containing `d` under policy (anchor, intervalWeeks).
// `end` is EXCLUSIVE and is also the date the next session unlocks.
// `index` is negative when d precedes the anchor, which means the cadence has
// not started yet for that group (Part C.5) -- callers should check
// `beforeAnchor` rather than assuming a block is meaningful.
export function blockBounds(d, { anchor, intervalWeeks }) {
  const length = 7 * intervalWeeks;
  const index = Math.floor(diffDays(d, anchor) / length);
  const start = addDays(anchor, index * length);
  return {
    start,
    end: addDays(start, length),
    index,
    beforeAnchor: index < 0,
  };
}

// Convenience wrapper taking a policy row as the backend will return it
// ({ effective_from, interval_weeks }). effective_from doubles as the anchor
// (Part D.2), which is why there is no separate anchor field.
export function blockForPolicy(d, policy) {
  return blockBounds(d, {
    anchor: parseDate(policy.effective_from),
    intervalWeeks: policy.interval_weeks,
  });
}

// -- Policy resolution (Part D.3) -------------------------------------------

// "The cadence governing date D is the row with the greatest effective_from
// <= D." Returns null when the group has no policy in force on that date,
// which covers both an unconfigured group and a date before its first policy.
export function resolvePolicy(policies, onDate) {
  let best = null;
  for (const p of policies || []) {
    const from = parseDate(p.effective_from);
    if (!from || from > onDate) continue;
    if (!best || from > parseDate(best.effective_from)) best = p;
  }
  return best;
}

// Rows that have not taken effect yet. These are the only mutable ones
// (Part D.4 Case 6). Sorted soonest-first.
export function pendingPolicies(policies, onDate) {
  return (policies || [])
    .filter((p) => {
      const from = parseDate(p.effective_from);
      return from && from > onDate;
    })
    .sort((a, b) => parseDate(a.effective_from) - parseDate(b.effective_from));
}

// Full history newest-first with a lifecycle status attached, which is what
// the timeline in View 2 renders:
//   scheduled  -- future, still editable/withdrawable
//   active     -- currently governing
//   superseded -- past, but still governs its own date range; never deleted
export function policyHistory(policies, onDate) {
  const active = resolvePolicy(policies, onDate);
  return (policies || [])
    .slice()
    .sort((a, b) => parseDate(b.effective_from) - parseDate(a.effective_from))
    .map((p) => ({
      ...p,
      status:
        parseDate(p.effective_from) > onDate
          ? "scheduled"
          : active && p.id === active.id
          ? "active"
          : "superseded",
      locked: parseDate(p.effective_from) <= onDate,
    }));
}

// -- Formatting -------------------------------------------------------------

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

// "Aug 3" / "Aug 3, 2026". Year is shown only when asked, so the dense table
// in View 1 stays readable while the history timeline stays unambiguous.
export function formatDate(date, { withYear = false } = {}) {
  if (!date) return "—";
  const base = `${MONTHS[date.getUTCMonth()]} ${date.getUTCDate()}`;
  return withYear ? `${base}, ${date.getUTCFullYear()}` : base;
}

// A block rendered the way a human reads a period: INCLUSIVE end date, even
// though `end` is stored exclusive. A 2-week block [Jul 20, Aug 3) shows as
// "Jul 20 – Aug 2". Getting this wrong by a day is the easiest way to make the
// page quietly lie about when a period closes.
export function formatBlock(block, opts) {
  if (!block) return "—";
  return `${formatDate(block.start, opts)} – ${formatDate(
    addDays(block.end, -1),
    opts
  )}`;
}

export function formatInterval(weeks) {
  return weeks === 1 ? "1 week" : `${weeks} weeks`;
}

// Plain-language length of a date range, used to label truncated/orphan
// windows in the preview ("2 days").
export function formatSpanDays(days) {
  return days === 1 ? "1 day" : `${days} days`;
}
